import type React from "react"
import { useEffect } from "react"

import type { ThemeBottomSectionImageItem, ThemeImageBannerRef } from "@/models/ITrackingPageOptions"
import stores from "@/stores"

interface UseSyncEditingImageOnCommonImageDeletedParams {
  /** Edit image 二级弹窗是否打开（关闭时无需订阅，且本地草稿会在关闭时重置） */
  active: boolean
  setImageDraftBanner: React.Dispatch<React.SetStateAction<ThemeImageBannerRef | undefined>>
  setImageSnapshot: React.Dispatch<React.SetStateAction<ThemeBottomSectionImageItem | undefined>>
}

/**
 * Edit image 二级弹窗打开期间，订阅 common image 删除事件：
 * 弹窗内的 banner 是进入编辑时拷贝的本地快照，不会随 store 剪枝自动更新。
 * 若被删除的 gid 正是弹窗内正在编辑的那张，则就地清空草稿与快照，使弹窗内图片与图库同步；
 * 草稿与快照一并清空，避免这次删除被记为弹窗内的未保存改动（与父级「active + 元数据都剪」一致）。
 *
 * 注：删除的若是弹窗内未在编辑的其它图片，由父级剪枝 store 配置 + `images` 经 context 响应式更新覆盖，
 * 此 hook 只负责处理「正在编辑的那张被删除」这一本地快照不会自动同步的情况。
 */
export const useSyncEditingImageOnCommonImageDeleted = ({
  active,
  setImageDraftBanner,
  setImageSnapshot,
}: UseSyncEditingImageOnCommonImageDeletedParams): void => {
  useEffect(() => {
    if (!active) {
      return
    }

    return stores.themeLayoutConfigStore.onCommonImageDeleted((gid) => {
      setImageDraftBanner((prev) => (prev?.shopifyFileId === gid ? undefined : prev))
      setImageSnapshot((prev) =>
        prev?.image?.shopifyFileId === gid ? { ...prev, image: undefined } : prev,
      )
    })
  }, [active, setImageDraftBanner, setImageSnapshot])
}
